import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Collapse, Drawer, IconButton, InputAdornment, Skeleton, TextField, Typography } from '@mui/material';
import {
    CloseRounded as CloseIcon,
    ManageSearchRounded as LookupIcon,
    VisibilityRounded as ViewOnlyIcon,
    SearchRounded as SearchIcon,
    CheckCircleRounded as DeliveredIcon,
    HourglassTopRounded as PendingIcon,
    DoNotDisturbOnRounded as CancelledIcon,
    RemoveCircleOutlineRounded as NoneIcon,
    FreeBreakfastRounded as BreakfastIcon,
    LunchDiningRounded as LunchIcon,
    DinnerDiningRounded as DinnerIcon,
    Groups2Rounded as GroupIcon,
    ExpandMoreRounded as ExpandIcon,
    PersonSearchRounded as EmptyIcon,
    ErrorOutlineRounded as ErrorIcon,
    TimerRounded as TimerIcon,
    ConfirmationNumberRounded as TokenIcon,
} from '@mui/icons-material';
import { LookupReservation, LookupUser, MealType, ReservationLookupResult, reservationsAPI } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { formatPersianDate } from '../../utils/dateHelper';
import { M, MEAL_LABELS, MEAL_ORDER, Tone, formatClock, initials, normalizeFa } from './monitorTokens';
import { MealThumb, StatusPill } from './MonitorParts';

const IDLE_CLOSE_SECONDS = 90;
const WARN_BEFORE_SECONDS = 15;

const MEAL_ICONS: Record<MealType, React.ReactElement> = {
    breakfast: <BreakfastIcon />,
    lunch: <LunchIcon />,
    dinner: <DinnerIcon />,
};

const ROLE_LABELS: Record<string, string> = { guest: 'مهمان', manager: 'مدیر', admin: 'مدیر سیستم' };

const MATCH_LABELS: Record<ReservationLookupResult['matchType'], string> = {
    exact: 'تطابق کامل کارت یا کد پرسنلی',
    partial: 'بخشی از کد پرسنلی یا شماره کارت',
    name: 'جستجو بر اساس نام',
    group: 'تحویل گروهی',
    token: 'ژتون مهمان',
    none: '',
};

const statusOf = (r?: LookupReservation): { tone: Tone | 'muted'; label: string; icon: React.ReactElement } => {
    if (!r) return { tone: 'muted', label: 'رزرو ندارد', icon: <NoneIcon /> };
    if (r.isCancelled) return { tone: 'danger', label: 'لغو شده', icon: <CancelledIcon /> };
    if (r.isDelivered) return { tone: 'success', label: r.deliveredAt ? `تحویل شده · ${formatClock(r.deliveredAt)}` : 'تحویل شده', icon: <DeliveredIcon /> };
    return { tone: 'warning', label: 'در انتظار تحویل', icon: <PendingIcon /> };
};

const StatusBadge: React.FC<{ reservation?: LookupReservation }> = ({ reservation }) => {
    const s = statusOf(reservation);
    if (s.tone === 'muted') {
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: M.textFaint, fontFamily: M.font, fontSize: '0.85rem', '& svg': { fontSize: '1rem' } }}>
                {s.icon}{s.label}
            </Box>
        );
    }
    return <StatusPill tone={s.tone} icon={s.icon}>{s.label}</StatusPill>;
};

// The meal of the current service shown first, and an active reservation preferred over a cancelled one
const reservationFor = (user: LookupUser, mealType: MealType) => {
    const list = user.today.filter((r) => r.mealType === mealType);
    return list.find((r) => !r.isCancelled) ?? list[0];
};

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: M.font, fontSize: '0.8rem', color: M.textFaint }}>{label}</Typography>
        <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>{value}</Typography>
    </Box>
);

const LookupUserCard: React.FC<{ user: LookupUser; mealType: MealType; expanded: boolean; onToggle: () => void; collapsible: boolean }> = ({
    user, mealType, expanded, onToggle, collapsible,
}) => {
    const current = reservationFor(user, mealType);
    const roleLabel = ROLE_LABELS[user.role];

    return (
        <Box sx={{ bgcolor: M.surfaceRaised, border: `1px solid ${expanded ? M.borderStrong : M.border}`, borderRadius: M.radiusCard, overflow: 'hidden' }}>
            <ButtonBase
                onClick={collapsible ? onToggle : undefined}
                disabled={!collapsible}
                aria-expanded={collapsible ? expanded : undefined}
                sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, textAlign: 'start', '&.Mui-focusVisible': { outline: `3px solid ${M.info}`, outlineOffset: -3 } }}
            >
                <Box sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: '50%', bgcolor: user.isActive ? M.infoSoft : M.dangerSoft, color: user.isActive ? M.infoText : M.dangerText, display: 'grid', placeItems: 'center', fontFamily: M.font, fontWeight: 800, fontSize: '1.1rem' }}>
                    {initials(user.firstName, user.lastName)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: M.font, fontWeight: 800, color: M.text, fontSize: '1.1rem' }}>
                            {user.firstName} {user.lastName}
                        </Typography>
                        {!user.isActive && <StatusPill tone="danger">غیرفعال</StatusPill>}
                        {roleLabel && <StatusPill tone="primary">{roleLabel}</StatusPill>}
                    </Box>
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>
                        {user.personnelCode ? `کد پرسنلی ${toPersianNumber(user.personnelCode)}` : 'بدون کد پرسنلی'}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'end' }}>
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.75rem', color: M.textFaint, mb: 0.25 }}>{MEAL_LABELS[mealType]} امروز</Typography>
                    <StatusBadge reservation={current} />
                </Box>
                {collapsible && <ExpandIcon sx={{ color: M.textFaint, transition: 'transform 200ms', transform: expanded ? 'rotate(180deg)' : 'none' }} />}
            </ButtonBase>

            <Collapse in={expanded} timeout={200} unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.75, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                    {user.matched?.map((r) => (
                        <Box key={r.id} sx={{ p: 1.25, borderRadius: M.radiusControl, bgcolor: M.infoSoft, border: `1px solid ${M.info}55` }}>
                            <Typography sx={{ fontFamily: M.font, fontWeight: 800, color: M.infoText, display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                <TokenIcon fontSize="small" /> ژتون اسکن‌شده
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <MealThumb imageUrl={r.meal.imageUrl} title={r.meal.title} size={44} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>{r.meal.title}</Typography>
                                    <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>
                                        {MEAL_LABELS[r.mealType]} · {formatPersianDate(r.mealDate)}
                                    </Typography>
                                </Box>
                                <StatusBadge reservation={r} />
                            </Box>
                        </Box>
                    ))}

                    {!user.isActive && (
                        <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: M.dangerText, bgcolor: M.dangerSoft, borderRadius: M.radiusControl, px: 1.5, py: 1 }}>
                            این پرسنل غیرفعال است؛ اسکن کارتش در مانیتور پذیرفته نمی‌شود.
                        </Typography>
                    )}

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.25, bgcolor: M.bg, borderRadius: M.radiusControl, p: 1.25 }}>
                        <InfoItem label="شماره کارت" value={user.cardNumber ? toPersianNumber(user.cardNumber) : '—'} />
                        <InfoItem
                            label="کارت‌خوان"
                            value={<Box component="span" sx={{ color: user.hasCardSerial ? M.successText : M.textMuted }}>{user.hasCardSerial ? 'کارت ثبت شده' : 'ثبت نشده'}</Box>}
                        />
                        <InfoItem label="واحد" value={user.unitTitle || '—'} />
                        <InfoItem label="شرکت" value={user.company || '—'} />
                    </Box>

                    <Box>
                        <Typography sx={{ fontFamily: M.font, fontWeight: 800, color: M.text, mb: 1 }}>رزروهای امروز</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {MEAL_ORDER.map((mt) => {
                                const r = reservationFor(user, mt);
                                const isCurrent = mt === mealType;
                                return (
                                    <Box
                                        key={mt}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.25, p: 1, minHeight: 60, borderRadius: M.radiusControl,
                                            bgcolor: isCurrent ? M.infoSoft : M.bg, border: `1px solid ${isCurrent ? `${M.info}55` : 'transparent'}`,
                                        }}
                                    >
                                        <Box sx={{ width: 64, display: 'flex', alignItems: 'center', gap: 0.5, color: isCurrent ? M.infoText : M.textMuted, '& svg': { fontSize: '1.1rem' } }}>
                                            {MEAL_ICONS[mt]}
                                            <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', fontWeight: 700, color: 'inherit' }}>{MEAL_LABELS[mt]}</Typography>
                                        </Box>
                                        {r ? (
                                            <>
                                                <MealThumb imageUrl={r.meal.imageUrl} title={r.meal.title} size={40} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700, color: M.text }}>{r.meal.title}</Typography>
                                                    <Typography sx={{ fontFamily: M.font, fontSize: '0.8rem', color: M.textFaint }}>{r.isCorporate ? 'غذای سازمانی' : 'غذای آزاد'}</Typography>
                                                </Box>
                                            </>
                                        ) : (
                                            <Box sx={{ flex: 1 }} />
                                        )}
                                        <StatusBadge reservation={r} />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ fontFamily: M.font, fontWeight: 800, color: M.text, mb: 1 }}>هفت روز آینده</Typography>
                        {user.upcoming.length === 0 ? (
                            <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: M.textFaint }}>رزروی برای روزهای آینده ثبت نشده است</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {user.upcoming.map((r) => (
                                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 0.75, borderRadius: M.radiusControl, bgcolor: M.bg }}>
                                        <MealThumb imageUrl={r.meal.imageUrl} title={r.meal.title} size={36} />
                                        <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 600, color: M.text, flex: 1, minWidth: 0 }}>{r.meal.title}</Typography>
                                        <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted, whiteSpace: 'nowrap' }}>
                                            {MEAL_LABELS[r.mealType]} · {formatPersianDate(r.mealDate)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

const ReservationLookupDrawer: React.FC<{ open: boolean; onClose: () => void; mealType: MealType }> = ({ open, onClose, mealType }) => {
    const [text, setText] = useState('');
    const [result, setResult] = useState<ReservationLookupResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(IDLE_CLOSE_SECONDS);

    const inputRef = useRef<HTMLInputElement>(null);
    const requestRef = useRef(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const lastActivityRef = useRef(Date.now());

    const touch = useCallback(() => { lastActivityRef.current = Date.now(); }, []);

    const search = useCallback(async (raw: string) => {
        const q = raw.trim();
        clearTimeout(debounceRef.current);
        if (!q) {
            setResult(null);
            setError('');
            return;
        }
        const id = ++requestRef.current;
        setLoading(true);
        setError('');
        try {
            const data = await reservationsAPI.lookupReservations(q);
            if (id !== requestRef.current) return; // a newer search superseded this one
            setResult(data);
            setExpandedId(data.users.length === 1 ? data.users[0].id : null);
        } catch (err: any) {
            if (id !== requestRef.current) return;
            setResult(null);
            setError(err.response?.data?.error || 'استعلام انجام نشد. اتصال را بررسی کنید.');
        } finally {
            if (id === requestRef.current) setLoading(false);
            touch();
        }
    }, [touch]);

    // Reset every time the panel opens so a previous person's data is never left on screen
    useEffect(() => {
        if (!open) return;
        setText('');
        setResult(null);
        setError('');
        setExpandedId(null);
        lastActivityRef.current = Date.now();
        setSecondsLeft(IDLE_CLOSE_SECONDS);
    }, [open]);

    // Close by itself when left idle, so the delivery screen is not left in view-only mode
    useEffect(() => {
        if (!open) return;
        const t = setInterval(() => {
            const left = IDLE_CLOSE_SECONDS - Math.floor((Date.now() - lastActivityRef.current) / 1000);
            setSecondsLeft(left);
            if (left <= 0) onClose();
        }, 1000);
        return () => clearInterval(t);
    }, [open, onClose]);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const onChange = (value: string) => {
        setText(value);
        touch();
        clearTimeout(debounceRef.current);
        // names are searched while typing; card readers finish with Enter well before this fires
        if (normalizeFa(value).length >= 2) {
            debounceRef.current = setTimeout(() => search(value), 450);
        } else if (!value.trim()) {
            search('');
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        touch();
        if (e.key === 'Enter') {
            e.preventDefault();
            search(text);
            inputRef.current?.select(); // the next card scan replaces the text instead of appending
        }
    };

    const users = result?.users ?? [];
    const group = result?.group;
    const groupStats = group
        ? users.reduce(
            (acc, u) => {
                const r = reservationFor(u, mealType);
                if (r && !r.isCancelled) {
                    acc.reserved++;
                    if (r.isDelivered) acc.delivered++;
                }
                return acc;
            },
            { reserved: 0, delivered: 0 })
        : null;

    return (
        <Drawer
            // the theme is RTL, so MUI mirrors this: the sheet opens from the physical left (the end side)
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                dir: 'rtl',
                onPointerDown: touch,
                onScroll: touch,
                sx: { width: 'min(600px, 100vw)', bgcolor: M.surface, color: M.text, backgroundImage: 'none', borderInlineEnd: `1px solid ${M.borderStrong}` },
            } as any}
            slotProps={{ backdrop: { sx: { bgcolor: 'rgba(3,6,18,0.6)' } } }}
            SlideProps={{ onEntered: () => inputRef.current?.focus() }}
        >
            <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${M.border}`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: M.radiusControl, bgcolor: M.infoSoft, color: M.infoText, display: 'grid', placeItems: 'center' }}>
                        <LookupIcon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography component="h2" sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '1.35rem', color: M.text }}>استعلام رزرو</Typography>
                        <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>با کارت، کد پرسنلی یا نام</Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="بستن استعلام" sx={{ width: 48, height: 48, color: M.textMuted, borderRadius: M.radiusControl, '&:hover': { bgcolor: M.surfaceHover } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: M.radiusControl, bgcolor: M.infoSoft, border: `1px solid ${M.info}55`, color: M.infoText }}>
                    <ViewOnlyIcon fontSize="small" />
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', fontWeight: 700, color: 'inherit' }}>
                        فقط مشاهده — اسکن کارت در این پنجره غذا را تحویل نمی‌دهد
                    </Typography>
                </Box>

                <TextField
                    inputRef={inputRef}
                    fullWidth
                    value={text}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    placeholder="کارت را بکشید، یا کد پرسنلی / نام را بنویسید"
                    inputProps={{ 'aria-label': 'کارت، کد پرسنلی یا نام برای استعلام' }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: M.infoText }} /></InputAdornment>,
                        endAdornment: loading ? <InputAdornment position="end"><CircularProgress size={20} sx={{ color: M.infoText }} /></InputAdornment> : undefined,
                        sx: {
                            height: M.controlHeight, borderRadius: M.radiusControl, fontFamily: M.font, fontSize: '1.05rem', color: M.text, bgcolor: M.bg,
                            '& fieldset': { borderColor: M.borderStrong },
                            '&.Mui-focused fieldset': { borderColor: `${M.info} !important`, borderWidth: 2 },
                        },
                    }}
                />

                <Box sx={{ minHeight: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }} aria-live="polite">
                        {result && users.length > 0 && !group && `${toPersianNumber(users.length)} نتیجه · ${MATCH_LABELS[result.matchType]}`}
                        {result && group && MATCH_LABELS.group}
                    </Typography>
                    {secondsLeft <= WARN_BEFORE_SECONDS && (
                        <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.warningText, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimerIcon fontSize="small" /> بستن خودکار تا {toPersianNumber(Math.max(0, secondsLeft))} ثانیه
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, '& > *': { flexShrink: 0 } }}>
                {loading && !result && [0, 1].map((i) => (
                    <Skeleton key={i} variant="rounded" height={84} sx={{ bgcolor: M.surfaceRaised, borderRadius: M.radiusCard }} />
                ))}

                {!loading && !result && !error && (
                    <Box sx={{ textAlign: 'center', color: M.textMuted, mt: 6, px: 3 }}>
                        <LookupIcon sx={{ fontSize: 64, color: M.infoText, opacity: 0.8 }} />
                        <Typography sx={{ fontFamily: M.font, fontWeight: 700, color: M.text, mt: 1.5, fontSize: '1.1rem' }}>رزرو یک نفر را بدون تحویل بررسی کنید</Typography>
                        <Typography sx={{ fontFamily: M.font, mt: 1, lineHeight: 1.9 }}>
                            کارت را روی کارت‌خوان بگذارید، یا کد پرسنلی یا بخشی از نام را بنویسید.
                            وضعیت صبحانه، ناهار و شام امروز و رزروهای هفته‌ی آینده نمایش داده می‌شود.
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                        <ErrorIcon sx={{ fontSize: 56, color: M.dangerText }} />
                        <Typography sx={{ fontFamily: M.font, color: M.text, mt: 1 }}>{error}</Typography>
                        <Button
                            onClick={() => search(text)}
                            variant="outlined"
                            sx={{ mt: 2, height: M.controlHeight, borderRadius: M.radiusControl, fontFamily: M.font, color: M.infoText, borderColor: M.info }}
                        >
                            تلاش دوباره
                        </Button>
                    </Box>
                )}

                {result && users.length === 0 && !error && (
                    <Box sx={{ textAlign: 'center', mt: 5, px: 3 }}>
                        <EmptyIcon sx={{ fontSize: 60, color: M.textFaint }} />
                        <Typography sx={{ fontFamily: M.font, color: M.text, fontWeight: 700, mt: 1 }}>
                            {group ? 'این گروه عضوی ندارد' : `کسی با «${result.query}» پیدا نشد`}
                        </Typography>
                        {!group && (
                            <Typography sx={{ fontFamily: M.font, color: M.textMuted, mt: 1 }}>
                                اگر کارت است و به کسی متصل نیست، در صفحه‌ی اصلی اسکن کنید تا به پرسنل متصل شود.
                            </Typography>
                        )}
                    </Box>
                )}

                {group && groupStats && (
                    <Box sx={{ bgcolor: M.surfaceRaised, border: `1px solid ${M.borderStrong}`, borderRadius: M.radiusCard, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: M.radiusControl, bgcolor: M.primarySoft, color: M.primaryText, display: 'grid', placeItems: 'center' }}>
                            <GroupIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.15rem', color: M.text }}>{group.title}</Typography>
                                {group.isActive ? <StatusPill tone="success">فعال</StatusPill> : <StatusPill tone="danger">غیرفعال</StatusPill>}
                            </Box>
                            <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: M.textMuted }}>
                                {toPersianNumber(group.memberCount)} عضو · {MEAL_LABELS[mealType]} امروز: {toPersianNumber(groupStats.reserved)} رزرو، {toPersianNumber(groupStats.delivered)} تحویل‌شده، {toPersianNumber(groupStats.reserved - groupStats.delivered)} در انتظار
                            </Typography>
                        </Box>
                    </Box>
                )}

                {users.map((u) => (
                    <LookupUserCard
                        key={u.id}
                        user={u}
                        mealType={mealType}
                        collapsible={users.length > 1}
                        expanded={users.length === 1 || expandedId === u.id}
                        onToggle={() => { touch(); setExpandedId((prev) => (prev === u.id ? null : u.id)); }}
                    />
                ))}

                {result?.truncated && (
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: M.warningText, textAlign: 'center', py: 1 }}>
                        نتایج بیشتری هم هست؛ نام کامل‌تر یا کد پرسنلی را وارد کنید
                    </Typography>
                )}
            </Box>
        </Drawer>
    );
};

export default ReservationLookupDrawer;
